import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Box, Button, CircularProgress, Divider,
} from '@mui/material';
import CreditPackagePicker from './CreditPackagePicker';
import PaymentMethodChooser, { NEW_CARD } from './PaymentMethodChooser';
import { creditPackDialog } from '../utils/subscriptionCopy';
import { formatMoney } from '../utils/money';
import { isInlineCardEntryAvailable } from '../services/stripeClient';
import api from '../services/api';

/**
 * Buying a credit pack.
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 * A dialog titled "Add credits" holding three tiles that each BOUGHT on click.
 * Three problems, all fixed by separating choosing from paying:
 *
 *   • an irreversible payment was one stray tap away, with no confirm step;
 *   • there was nowhere to show which card would be charged, so a subscriber
 *     was thrown out to a hosted checkout page to re-enter a card Stripe
 *     already had;
 *   • the total was never stated on the control that committed to it.
 *
 * ── TWO AUDIENCES, ONE DIALOG ───────────────────────────────────────────────
 * A subscriber pays on the card on file and never leaves. A free-plan or trial
 * account goes to Stripe Checkout, where card entry and 3-D Secure are Stripe's
 * problem. Which one applies is `showsPaymentMethod`, from the same
 * isLiveSubscription the rest of the subscription copy uses.
 *
 * ── NOTHING HERE DECIDES A PRICE ────────────────────────────────────────────
 * `packages` arrive with `price_cents` already resolved to this account's tier
 * (subscribers pay less). The server re-resolves the tier from the account when
 * charging, so what is shown and what is taken come from one decision — this
 * component cannot ask for the cheaper rate, and neither can its caller.
 */
export default function CreditPackDialog({
  open,
  onClose,
  userId,
  subscription,
  packages = [],
  /**
   * Starts the purchase. Returns the server's response so this component can
   * tell a completed charge from a redirect. Owned by the caller because the
   * error dialog lives there.
   */
  onPurchase,
  /** Called after an in-modal charge succeeds, so the balance can refresh. */
  onPurchased,
  /**
   * OLD: the PLAN-granted credit balance, named in the subtitle. Still
   * accepted as a fallback; `balanceCredits` wins when both are given.
   */
  planCredits = null,
  /**
   * The account's whole credit balance, named in the subtitle so a subscriber
   * sees what this purchase is being added to ("your existing credit balance
   * of xxx credits"). Optional: the sentence drops the clause rather than
   * guessing when it is absent. See creditPackDialog.
   */
  balanceCredits = null,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);

  // ── Cards, for the subscriber path only ─────────────────────────────────
  const [methods, setMethods] = useState([]);
  const [pmLoading, setPmLoading] = useState(false);
  const [selectedPmId, setSelectedPmId] = useState(null);
  const [setupClientSecret, setSetupClientSecret] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const cardRef = useRef(null);

  const inlineCardsAvailable = isInlineCardEntryAvailable();
  const usingNewCard = selectedPmId === NEW_CARD;

  const selected = useMemo(
    () => packages.find((p) => p.id === selectedId) || null,
    [packages, selectedId],
  );

  const copy = creditPackDialog(
    subscription,
    selected ? formatMoney(selected.price_cents, 'usd') : null,
    // OLD: { planCredits },
    { planCredits, balanceCredits },
  );

  // Default to the FIRST pack whenever the dialog opens. An empty selection
  // would leave the confirm button dead on arrival with nothing explaining why,
  // and the smallest pack is the safe default to pre-select — it is the
  // cheapest thing the customer can accidentally agree to.
  useEffect(() => {
    if (!open) return;
    setSelectedId((current) => current ?? (packages[0]?.id ?? null));
  }, [open, packages]);

  // Reset per-attempt state on close, but keep nothing that would make a
  // re-open look mid-flight.
  useEffect(() => {
    if (open) return;
    setBusy(false);
    setCardError(null);
    setSelectedId(null);
  }, [open]);

  // Saved cards, fetched only for the path that shows them.
  useEffect(() => {
    if (!open || !copy.showsPaymentMethod || !userId) return undefined;
    let active = true;
    setPmLoading(true);

    (async () => {
      try {
        const result = await api.getPaymentMethods(userId);
        if (!active) return;
        const list = result?.payment_methods || [];
        setMethods(list);
        // Prefer the account default; otherwise the first card. With no card at
        // all, open straight on inline entry rather than a chooser with nothing
        // in it — that is the "paid account with no card on file" case.
        const preferred = list.find((m) => m.is_default) || list[0] || null;
        setSelectedPmId(preferred ? preferred.id : NEW_CARD);
      } catch (err) {
        if (active) setSelectedPmId(NEW_CARD);
      } finally {
        if (active) setPmLoading(false);
      }
    })();

    return () => { active = false; };
  }, [open, copy.showsPaymentMethod, userId]);

  // A SetupIntent is needed only to collect a NEW card.
  useEffect(() => {
    if (!open || !usingNewCard || !inlineCardsAvailable || setupClientSecret || !userId) {
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const result = await api.createSetupIntent(userId);
        if (active) setSetupClientSecret(result?.data?.client_secret || null);
      } catch (err) {
        // Inline entry is unavailable; the confirm still works by falling back
        // to Checkout server-side when no card can be charged.
        if (active) setSetupClientSecret(null);
      }
    })();
    return () => { active = false; };
  }, [open, usingNewCard, inlineCardsAvailable, setupClientSecret, userId]);

  const cardReady = !copy.showsPaymentMethod
    ? true
    : (!usingNewCard
      ? Boolean(selectedPmId)
      // With inline entry available the card has to be complete; without it the
      // server falls back to Checkout, so there is nothing to wait for here.
      : (inlineCardsAvailable ? cardComplete : true));

  const canConfirm = Boolean(selected) && cardReady && !busy && !pmLoading;

  const handleConfirm = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setCardError(null);
    try {
      // A newly entered card has to be saved before it can be charged. Same
      // contract the plan-change dialog uses: confirm() resolves to
      // { paymentMethodId } or { error }, and the card data itself never
      // reaches our server — only the resulting id does.
      let paymentMethodId = usingNewCard ? null : selectedPmId;
      if (copy.showsPaymentMethod && usingNewCard) {
        const outcome = await cardRef.current?.confirm();
        if (!outcome || outcome.error) {
          // A decline keeps the user HERE, with the pack still selected and the
          // form still mounted, rather than closing over the failure.
          setCardError(outcome?.error || 'That card could not be saved. Try another card.');
          return;
        }
        paymentMethodId = outcome.paymentMethodId;
      }

      const result = await onPurchase?.(selected.id, paymentMethodId);

      // A completed in-modal charge returns no redirect URL. A redirect (free
      // plan, or a subscriber whose card needed 3-D Secure) is handled by the
      // caller navigating away, so this component simply stops.
      if (result?.data?.paid) {
        onPurchased?.(result.data);
        onClose?.();
      }
    } catch (err) {
      // The caller surfaces purchase failures through the error dialog; a card
      // failure is shown inline, where the card is.
      setCardError(err?.message || 'That card could not be used. Try another.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      aria-labelledby="credit-pack-dialog-title"
    >
      <DialogTitle
        id="credit-pack-dialog-title"
        sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, color: '#1A1A1A' }}
      >
        {copy.title}
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
          {copy.subtitle}
        </DialogContentText>

        <CreditPackagePicker
          packages={packages}
          selectedId={selectedId}
          onSelect={setSelectedId}
          disabled={busy}
        />

        {/* The free tier's trade-off, stated under the packs it applies to.
            Null for a subscriber — see creditPackDialog. */}
        {copy.footnote && (
          <DialogContentText sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#8A8F85', mt: 2 }}>
            {copy.footnote}
          </DialogContentText>
        )}

        {copy.showsPaymentMethod && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <PaymentMethodChooser
              methods={methods}
              loading={pmLoading}
              selectedPmId={selectedPmId}
              onSelect={(id) => { setSelectedPmId(id); setCardError(null); }}
              inlineAvailable={inlineCardsAvailable}
              clientSecret={setupClientSecret}
              cardRef={cardRef}
              onCardValidityChange={setCardComplete}
              error={cardError}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, color: '#8A8F85' }}
        >
          {/* OLD: Cancel — "Not now" on the free tier, see creditPackDialog. */}
          {copy.cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canConfirm}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
        >
          {busy ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              Working…
            </Box>
          ) : copy.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
